import { useLanguage } from "@/hooks/useLanguage";
import { Button } from "@/components/ui/button";
import { ArrowRight, Chrome, Sparkles, Download, Star } from "lucide-react";

const Hero = () => {
  const { t } = useLanguage();

  const scrollToDownload = () => {
    const element = document.getElementById('download');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToDemo = () => {
    const element = document.getElementById('demo');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden">
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-transparent pointer-events-none" />
      
      <div className="relative max-w-3xl mx-auto text-center">
        {/* Product Hunt Badge */}
        <a 
          href="https://www.producthunt.com/posts/phonetic-pro" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ff6154]/10 border border-[#ff6154]/20 text-sm text-[#ff6154] mb-4 hover:bg-[#ff6154]/20 transition-colors animate-fade-in"
        >
          <svg className="w-5 h-5" viewBox="0 0 40 40" fill="currentColor">
            <path d="M20 40C31.0457 40 40 31.0457 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 31.0457 8.9543 40 20 40Z"/>
            <path d="M22.5 20H17.5V12.5H22.5C24.571 12.5 26.25 14.179 26.25 16.25C26.25 18.321 24.571 20 22.5 20Z" fill="white"/>
            <path d="M17.5 20H22.5C24.571 20 26.25 21.679 26.25 23.75C26.25 25.821 24.571 27.5 22.5 27.5H17.5V20Z" fill="white"/>
            <path d="M12.5 12.5H17.5V27.5H12.5V12.5Z" fill="white"/>
          </svg>
          <span className="font-medium">Featured on Product Hunt</span>
        </a>

        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-sm text-muted-foreground mb-6 animate-fade-in">
          <Sparkles className="w-3.5 h-3.5" />
          {t('heroBadge' as any) || 'Free Browser Extension'}
        </div>

        {/* Headline */}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          {t('heroTitle') || 'Convert Text to Phonetic Alphabets'}
        </h1>

        {/* Subtitle */}
        <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-10 leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
          {t('heroSubtitle') || 'Transform any text into NATO, ICAO, and other phonetic alphabets instantly. Perfect for clear communication.'}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in" style={{ animationDelay: '0.3s' }}>
          <Button
            size="lg"
            onClick={scrollToDownload}
            className="group gap-2"
          >
            <Chrome className="w-4 h-4" />
            {t('heroInstallChrome' as any) || 'Add to Chrome - Free'}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={scrollToDemo}
          >
            {t('heroWatchDemo' as any) || 'Try Demo'}
          </Button>
        </div>

        {/* Social proof */}
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="flex items-center gap-2">
            <div className="flex -space-x-2">
              {['S', 'M', 'J', 'A'].map((letter, i) => (
                <div 
                  key={i}
                  className="w-7 h-7 rounded-full bg-gradient-to-br from-primary/40 to-primary/60 border-2 border-background flex items-center justify-center text-xs font-medium text-primary-foreground"
                >
                  {letter}
                </div>
              ))}
            </div>
            <span>5,000+ users</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              ))}
            </div>
            <span>4.9/5 rating</span>
          </div>
          <div className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            <span>10K+ downloads</span>
          </div>
        </div>
      </div>

      {/* Preview Image */}
      <div className="relative max-w-4xl mx-auto mt-16 animate-fade-in" style={{ animationDelay: '0.5s' }}>
        <div className="bg-card rounded-xl border border-border overflow-hidden shadow-lg">
          <img
            src="/phonetic.gif"
            alt="Phonetic Pro Extension Preview"
            className="w-full h-auto"
          />
        </div>
      </div>
    </section>
  );
};

export default Hero;
